import {
  DEFAULT_CAROUSEL_PLAYBACK_SPEED,
  clampToCarouselPlaybackSpeed,
} from "@/config/carousel-playback-speed";
import { logCarouselPlaybackRate } from "@/lib/carouselPlaybackRateDebug";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface VideoSwipeContextValue {
  videoRef: React.RefObject<HTMLVideoElement>; // Header-Video (Marketing-Seiten)
  choreographyVideoRef: React.RefObject<HTMLVideoElement>; // Choreographie-Videos (Dashboard) – zeigt auf den AKTIVEN Slot
  choreographyVideoRef2: React.RefObject<HTMLVideoElement>; // Zweiter Crossfade-Slot (für rAF-Sync)
  swipeProgress: number; // 0-1, wie weit der Wischer ist
  isSwiping: boolean;
  isDashboardLoaded: boolean; // Ob das Dashboard vollständig geladen ist
  isSlowMotion: boolean; // Ob das Video gerade in Slow-Motion abgespielt wird
  setSwipeProgress: (progress: number) => void;
  setIsSwiping: (swiping: boolean) => void;
  setDashboardLoaded: (loaded: boolean) => void;
  stopVideo: () => void;
  closeCarousel: (options?: { animated?: boolean }) => void;
  playVideoSlowMotion: (
    targetTime: number,
    onNormalSpeedReached?: () => void,
    useChoreographyVideo?: boolean // Wenn true, verwende Choreographie-Video statt Header-Video
  ) => void; // Video 5 Sekunden zurück, 70% Geschwindigkeit bis targetTime, dann Callback
  resetVideoSpeed: () => void; // Setzt Video-Geschwindigkeit auf 1.0 zurück
  cancelSlowMotion: () => void; // Bricht Slow-Motion ab OHNE Callback aufzurufen
  getActiveVideoRef: () => React.RefObject<HTMLVideoElement> | null; // Gibt den aktiven Video-Ref zurück (Dashboard = Choreographie, sonst = Header)
  setChoreographyVideoController: (
    controller: {
      goToPreviousVideo: () => void;
      goToNextVideo: () => void;
      getCurrentVideoIndex: () => number;
      getVideosCount: () => number;
    } | null
  ) => void; // Setzt Controller für Choreographie-Videos
  getChoreographyVideoController: () => {
    goToPreviousVideo: () => void;
    goToNextVideo: () => void;
    getCurrentVideoIndex: () => number;
    getVideosCount: () => number;
  } | null; // Gibt Controller für Choreographie-Videos zurück
  setCurrentChoreographyVideoId: (videoId: string | null) => void; // Setzt aktuelle Video-ID
  getCurrentChoreographyVideoId: () => string | null; // Gibt aktuelle Video-ID zurück
  setPrevChoreographyVideoId: (videoId: string | null) => void; // Setzt vorherige Video-ID (prefetched)
  getPrevChoreographyVideoId: () => string | null; // Gibt vorherige Video-ID zurück
  setNextChoreographyVideoId: (videoId: string | null) => void; // Setzt nächste Video-ID (prefetched)
  getNextChoreographyVideoId: () => string | null; // Gibt nächste Video-ID zurück
  /** Startzeit des aktuell abgespielten Segments (für Step-Vergleich mit normalisierten figure_marks) */
  choreographySegmentStartTime: number | null;
  setChoreographySegmentStartTime: (time: number | null) => void;
  /** Endzeit des aktuell abgespielten Segments (optional; wenn gesetzt: Segmentgrenze für UI/Timeline) */
  choreographySegmentEndTime: number | null;
  setChoreographySegmentEndTime: (time: number | null) => void;
  /** Expliziter Zeit-Offset für Timeline/Seek (statt Auto-Erkennung) */
  choreographyTimeOffset: number;
  setChoreographyTimeOffset: (offset: number) => void;
  /** Welcher Video-Slot (0/1) ist aktuell aktiv (Crossfade/Loop) */
  choreographyActiveSlot: number;
  setChoreographyActiveSlot: (slot: number) => void;
  /** Wird erhöht wenn choreographyVideoRef gesetzt wird – löst Re-Render in ChoreographyVideoElementDisplay aus */
  choreographyVideoReadyVersion: number;
  notifyChoreographyVideoReady: () => void;
  /** Karussell-Lernmodus: 0.5 | 0.75 | 1 | "sync". Bei 1/sync kein Verlangsamen. */
  carouselPlaybackSpeed: number | "sync";
  setCarouselPlaybackSpeed: (speed: number | "sync") => void;
  /** Karussell stumm: Musik aus, keine Timing-Korrektur, Video in Originalgeschwindigkeit. */
  carouselMuted: boolean;
  setCarouselMuted: (muted: boolean) => void;
  /**
   * Nur bei Workshop-/Event-Wrap-up mit Event-Zuweisung (siehe `isWrapUpEventGateOriginalAudioVideo`):
   * Originalton des Videos stumm/laut.
   * Standard false = Ton an.
   */
  sessionHighlightVideoAudioMuted: boolean;
  setSessionHighlightVideoAudioMuted: (muted: boolean) => void;
  /**
   * Oberkante der Karussell-Steuerleiste (Move/Choreo/Pfeile …), `getBoundingClientRect().top`.
   * Wird von `CarouselVideoControls` gemessen; Overlays können `bottom` daran ausrichten.
   */
  carouselChromeMinTopPx: number | null;
  reportCarouselChromeMinTop: (topPx: number | null) => void;
}

const VideoSwipeContext = createContext<VideoSwipeContextValue | null>(null);

export function VideoSwipeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement>(null); // Header-Video
  const choreographyVideoRef = useRef<HTMLVideoElement>(null); // Choreographie-Videos – aktiver Slot
  const choreographyVideoRef2 = useRef<HTMLVideoElement>(null); // Zweiter Crossfade-Slot
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isDashboardLoaded, setDashboardLoaded] = useState(false);
  const [isSlowMotion, setIsSlowMotion] = useState(false);
  const [useChoreographyVideo, setUseChoreographyVideo] = useState(false); // Flag: Verwende Choreographie-Video für Slow-Motion
  const choreographyVideoControllerRef = useRef<{
    goToPreviousVideo: () => void;
    goToNextVideo: () => void;
    getCurrentVideoIndex: () => number;
    getVideosCount: () => number;
  } | null>(null);
  const [currentChoreographyVideoId, setCurrentChoreographyVideoIdState] =
    useState<string | null>(null);
  const [prevChoreographyVideoId, setPrevChoreographyVideoIdState] = useState<
    string | null
  >(null);
  const [nextChoreographyVideoId, setNextChoreographyVideoIdState] = useState<
    string | null
  >(null);
  const [choreographySegmentStartTime, setChoreographySegmentStartTimeState] =
    useState<number | null>(null);
  const [choreographySegmentEndTime, setChoreographySegmentEndTimeState] =
    useState<number | null>(null);
  const [choreographyTimeOffset, setChoreographyTimeOffsetState] =
    useState<number>(0);
  const [choreographyActiveSlot, setChoreographyActiveSlotState] =
    useState<number>(0);
  const [choreographyVideoReadyVersion, setChoreographyVideoReadyVersion] =
    useState(0);
  const [carouselPlaybackSpeed, setCarouselPlaybackSpeedState] = useState(
    DEFAULT_CAROUSEL_PLAYBACK_SPEED
  );
  const [carouselMuted, setCarouselMutedState] = useState(false);
  const [sessionHighlightVideoAudioMuted, setSessionHighlightVideoAudioMuted] =
    useState(false);
  const [carouselChromeMinTopPx, setCarouselChromeMinTopPx] = useState<
    number | null
  >(null);

  const reportCarouselChromeMinTop = useCallback((topPx: number | null) => {
    setCarouselChromeMinTopPx(prev => (prev === topPx ? prev : topPx));
  }, []);

  const basePlaybackRate =
    typeof carouselPlaybackSpeed === "number" ? carouselPlaybackSpeed : 1;
  const basePlaybackRateRef = useRef<number>(basePlaybackRate);
  const carouselSettingRef = useRef<number | "sync">(carouselPlaybackSpeed);
  useEffect(() => {
    basePlaybackRateRef.current = basePlaybackRate;
    carouselSettingRef.current = carouselPlaybackSpeed;
  }, [basePlaybackRate, carouselPlaybackSpeed]);

  // Refs für Cleanup von Timern und Event-Listenern
  const slowMotionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const slowMotionListenerRef = useRef<(() => void) | null>(null);
  const slowMotionCallbackRef = useRef<(() => void) | null>(null);

  // Gibt den aktiven Video-Ref zurück (Dashboard = Choreographie, sonst = Header)
  const getActiveVideoRef = (): React.RefObject<HTMLVideoElement> | null => {
    // Wenn Choreographie-Video vorhanden ist (auch wenn nicht in Slow-Motion), verwende es
    // Das Dashboard verwendet immer Choreographie-Videos
    if (choreographyVideoRef.current) {
      return choreographyVideoRef;
    }
    // Sonst Header-Video
    if (videoRef.current) {
      return videoRef;
    }
    return null;
  };

  const stopVideo = () => {
    const activeRef = getActiveVideoRef();
    if (activeRef?.current) {
      activeRef.current.pause();
    }
  };

  // Reset-Funktion: Setzt Video-Geschwindigkeit auf Basis-Rate zurück (Karussell-Setting)
  const resetVideoSpeed = () => {
    const activeRef = getActiveVideoRef();
    if (activeRef?.current) {
      const video = activeRef.current;
      const base = basePlaybackRateRef.current;
      if (video.playbackRate !== base) {
        logCarouselPlaybackRate(
          "VideoSwipeContext.resetVideoSpeed",
          base,
          carouselSettingRef.current
        );
        video.playbackRate = base;
      }
    }

    // Slow-Motion State zurücksetzen
    setIsSlowMotion(false);
    setUseChoreographyVideo(false);

    // Cleanup: Timer und Listener entfernen
    if (slowMotionTimeoutRef.current) {
      clearTimeout(slowMotionTimeoutRef.current);
      slowMotionTimeoutRef.current = null;
    }

    const currentRef = getActiveVideoRef();
    if (slowMotionListenerRef.current && currentRef?.current) {
      currentRef.current.removeEventListener(
        "timeupdate",
        slowMotionListenerRef.current
      );
      slowMotionListenerRef.current = null;
    }

    // Callback aufrufen, falls vorhanden
    if (slowMotionCallbackRef.current) {
      slowMotionCallbackRef.current();
      slowMotionCallbackRef.current = null;
    }
  };

  // Cancel-Funktion: Bricht Slow-Motion ab OHNE Callback aufzurufen
  // (wird verwendet, wenn während der Slow-Motion ein neuer Swipe startet)
  const cancelSlowMotion = () => {
    const activeRef = getActiveVideoRef();
    if (activeRef?.current) {
      const video = activeRef.current;
      const base = basePlaybackRateRef.current;
      if (video.playbackRate !== base) {
        logCarouselPlaybackRate(
          "VideoSwipeContext.cancelSlowMotion",
          base,
          carouselSettingRef.current
        );
        video.playbackRate = base;
      }
    }

    // Slow-Motion State zurücksetzen
    setIsSlowMotion(false);
    setUseChoreographyVideo(false);

    // Cleanup: Timer und Listener entfernen
    if (slowMotionTimeoutRef.current) {
      clearTimeout(slowMotionTimeoutRef.current);
      slowMotionTimeoutRef.current = null;
    }

    const currentRef = getActiveVideoRef();
    if (slowMotionListenerRef.current && currentRef?.current) {
      currentRef.current.removeEventListener(
        "timeupdate",
        slowMotionListenerRef.current
      );
      slowMotionListenerRef.current = null;
    }

    // WICHTIG: Callback NICHT aufrufen - der neue Swipe soll verarbeitet werden
    // Nur den Callback-Ref löschen, damit er nicht später noch aufgerufen wird
    slowMotionCallbackRef.current = null;
  };

  /**
   * Berechnet die Startposition für Slow-Motion, die 5 Sekunden vor targetTime liegt.
   * Funktioniert über Video-Grenzen hinweg:
   * - Bei einem einzelnen Video: Loop-Logik (vom Ende zurück)
   * - Bei mehreren Videos: Ins Vorvideo springen (später erweiterbar)
   */
  const calculateSlowMotionStart = (
    targetTime: number,
    videoDuration: number
  ): number => {
    const JUMP_SECONDS = 5;
    const slowEnd = Math.min(targetTime, videoDuration);
    const slowStart = slowEnd - JUMP_SECONDS;

    // Wenn wir über den Anfang hinaus wollen
    if (slowStart < 0) {
      // Loop-Logik: Vom Ende des Videos zurück
      // z.B. targetTime = 2s, duration = 10s → slowStart = -3s
      // → Loop: 10s - 3s = 7s (vom Ende 3s zurück)
      const overshoot = -slowStart;
      const loopedStart = videoDuration - overshoot;

      // Falls overshoot größer als die Video-Dauer ist, weiter im Loop
      if (loopedStart < 0) {
        // Mehrfacher Loop (sehr unwahrscheinlich, aber möglich)
        return videoDuration + (loopedStart % videoDuration);
      }

      return loopedStart;
    }

    return slowStart;
  };

  const playVideoSlowMotion = (
    targetTime: number,
    onNormalSpeedReached?: () => void,
    useChoreography = false
  ) => {
    // Bestimme welches Video verwendet werden soll
    const targetRef = useChoreography ? choreographyVideoRef : videoRef;
    if (!targetRef.current) return;

    // Alte Slow-Motion-Session sauber beenden
    resetVideoSpeed();

    // Setze Flag für Choreographie-Video
    setUseChoreographyVideo(useChoreography);

    const video = targetRef.current;
    const hasValidDuration =
      Number.isFinite(video.duration) && video.duration > 0;

    if (!hasValidDuration) {
      console.warn(
        "🎬 Video hat keine gültige Dauer, kann Slow-Motion nicht starten"
      );
      return;
    }

    // Ende der Slow-Motion ist die aktuelle Position (oder das Ende des Videos)
    const slowEnd = Math.min(targetTime, video.duration);

    // Start ist 5 Sekunden davor - kann über Video-Grenzen hinausgehen (Loop-Logik)
    const slowStart = calculateSlowMotionStart(targetTime, video.duration);

    console.log("🎬 Slow-Motion: Bereich", slowStart, "->", slowEnd);

    // Startzeit setzen
    video.currentTime = slowStart;
    // Slow-Motion darf die Nutzer-Geschwindigkeit nicht "überholen"
    const slowRate = Math.min(0.7, basePlaybackRateRef.current);
    logCarouselPlaybackRate(
      "VideoSwipeContext.playVideoSlowMotion",
      slowRate,
      carouselSettingRef.current
    );
    video.playbackRate = slowRate;

    // Slow-Motion State aktivieren
    setIsSlowMotion(true);

    // Callback speichern
    slowMotionCallbackRef.current = onNormalSpeedReached || null;

    // Prüfe ob wir eine Loop-Situation haben (slowStart > slowEnd)
    const isLooping = slowStart > slowEnd;
    let lastCheckedTime = slowStart; // Tracke die letzte Position für Loop-Erkennung
    let hasPassedLoop = false; // Flag: Haben wir den Loop-Punkt (Ende → Anfang) bereits passiert?

    // Speichere useChoreography im Closure
    const isChoreographyVideo = useChoreography;

    // Event Listener: Wenn slowEnd erreicht ist → alles zurücksetzen
    const handleTimeUpdate = () => {
      const currentVideoRef = isChoreographyVideo
        ? choreographyVideoRef
        : videoRef;
      if (!currentVideoRef.current) return;

      const currentTime = currentVideoRef.current.currentTime;

      if (isLooping) {
        // Loop-Situation: slowStart > slowEnd
        // z.B. slowStart = 7s, slowEnd = 2s, duration = 10s
        // Pfad: 7s → 10s (Loop) → 0s → 2s

        // Prüfe ob wir den Loop-Punkt passiert haben (vom Ende zum Anfang)
        // Das passiert, wenn currentTime deutlich kleiner ist als die letzte Position
        // und wir waren vorher nahe am Ende
        if (!hasPassedLoop && currentVideoRef.current) {
          const duration = currentVideoRef.current.duration;
          const wasNearEnd = lastCheckedTime > duration * 0.8; // War nahe am Ende (>80%)
          const jumpedToStart = currentTime < duration * 0.2; // Jetzt nahe am Anfang (<20%)

          if (wasNearEnd && jumpedToStart) {
            // Wir sind vom Ende zum Anfang gesprungen (Loop)
            hasPassedLoop = true;
            console.log("🎬 Loop erkannt: Video sprang vom Ende zum Anfang");
          }
        }

        // Wenn wir den Loop passiert haben und jetzt bei slowEnd oder darüber sind
        if (hasPassedLoop && currentTime >= slowEnd) {
          console.log(
            "🎬 Slow-Motion Ende erreicht bei:",
            currentTime,
            "(nach Loop)"
          );
          resetVideoSpeed();
        }

        // Aktualisiere lastCheckedTime
        if (currentTime > lastCheckedTime || hasPassedLoop) {
          lastCheckedTime = currentTime;
        }
      } else {
        // Normale Situation: slowStart < slowEnd
        if (currentTime >= slowEnd) {
          console.log("🎬 Slow-Motion Ende erreicht bei:", currentTime);
          resetVideoSpeed();
        }
      }
    };

    // Listener speichern & registrieren
    slowMotionListenerRef.current = handleTimeUpdate;
    video.addEventListener("timeupdate", handleTimeUpdate);

    // Fallback-Timer, falls `timeupdate` nicht zuverlässig feuert
    // Bei Loop-Situation: Dauer = (duration - slowStart) + slowEnd
    // Bei normaler Situation: Dauer = slowEnd - slowStart
    const slowMotionDurationSeconds = isLooping
      ? video.duration - slowStart + slowEnd
      : slowEnd - slowStart;
    const realTimeDurationSeconds =
      slowMotionDurationSeconds > 0 ? slowMotionDurationSeconds / 0.7 : 0;

    const fallbackDelay = Math.min(
      realTimeDurationSeconds * 1000 + 2000, // +2s Puffer
      10000 // max. 10s
    );

    if (fallbackDelay > 0) {
      slowMotionTimeoutRef.current = setTimeout(() => {
        console.log(
          "🎬 Fallback-Timer: Video-Geschwindigkeit wird zurückgesetzt (nach",
          fallbackDelay,
          "ms)"
        );
        resetVideoSpeed();
      }, fallbackDelay);
    }

    // Video abspielen
    video.play().catch(e => {
      console.log("🎬 Video slow motion play failed:", e);
      // Bei Fehler auch zurücksetzen
      resetVideoSpeed();
    });
  };

  const setChoreographyVideoController = (
    controller: {
      goToPreviousVideo: () => void;
      goToNextVideo: () => void;
      getCurrentVideoIndex: () => number;
      getVideosCount: () => number;
    } | null
  ) => {
    choreographyVideoControllerRef.current = controller;
  };

  const getChoreographyVideoController = () => {
    return choreographyVideoControllerRef.current;
  };

  const setCurrentChoreographyVideoId = (videoId: string | null) => {
    setCurrentChoreographyVideoIdState(prev => {
      // Bei Video-Wechsel: Segment-Daten zurücksetzen (verhindert stale Offsets)
      if (prev !== videoId) {
        setChoreographySegmentStartTimeState(null);
        setChoreographySegmentEndTimeState(null);
        setChoreographyTimeOffsetState(0);
      }
      return videoId;
    });
  };

  const getCurrentChoreographyVideoId = () => {
    return currentChoreographyVideoId;
  };

  const setPrevChoreographyVideoId = (videoId: string | null) => {
    setPrevChoreographyVideoIdState(videoId);
  };

  const getPrevChoreographyVideoId = () => {
    return prevChoreographyVideoId;
  };

  const setNextChoreographyVideoId = (videoId: string | null) => {
    setNextChoreographyVideoIdState(videoId);
  };

  const getNextChoreographyVideoId = () => {
    return nextChoreographyVideoId;
  };

  const closeCarousel = useCallback((options?: { animated?: boolean }) => {
    const animated = options?.animated ?? false;

    // Video NICHT pausieren beim Schließen des Karussells –
    // das Video läuft im Hintergrund weiter. Ein pause() hier
    // würde das Video stoppen und erst nach bis zu 5 s
    // vom Watchdog-Interval wieder gestartet werden.

    if (animated) {
      // Animierte Version: langsam zurück (falls gewünscht)
      // Aktuell nicht implementiert, da wir instant bevorzugen
      // Kann später hinzugefügt werden, wenn nötig
      setSwipeProgress(0);
    } else {
      // SOFORT alles zurücksetzen (keine Animation)
      setSwipeProgress(0);
    }

    setIsSwiping(false);
    setIsSlowMotion(false);

    // Single-Loop deaktivieren, damit beim nächsten Öffnen alle Videos wieder rotieren
    window.dispatchEvent(
      new CustomEvent("carousel:single-loop", {
        detail: { enabled: false },
      })
    );

    // IDs zurücksetzen für sauberen Zustand
    setCurrentChoreographyVideoId(null);
    setPrevChoreographyVideoId(null);
    setNextChoreographyVideoId(null);
    setChoreographySegmentStartTime(null);
    setChoreographySegmentEndTime(null);
    setChoreographyTimeOffsetState(0);
    setChoreographyActiveSlotState(0);
    setCarouselPlaybackSpeedState(DEFAULT_CAROUSEL_PLAYBACK_SPEED);
    setCarouselChromeMinTopPx(null);
    setSessionHighlightVideoAudioMuted(false);
  }, []);

  const setChoreographySegmentStartTime = useCallback(
    (time: number | null) => setChoreographySegmentStartTimeState(time),
    []
  );

  const setChoreographySegmentEndTime = useCallback(
    (time: number | null) => setChoreographySegmentEndTimeState(time),
    []
  );

  const setChoreographyTimeOffset = useCallback((offset: number) => {
    const safe =
      typeof offset === "number" && Number.isFinite(offset) && offset >= 0
        ? offset
        : 0;
    setChoreographyTimeOffsetState(safe);
  }, []);

  const setChoreographyActiveSlot = useCallback((slot: number) => {
    const normalized =
      typeof slot === "number" && Number.isFinite(slot) ? Math.round(slot) : 0;
    setChoreographyActiveSlotState(normalized === 1 ? 1 : 0);
  }, []);

  const notifyChoreographyVideoReady = useCallback(() => {
    setChoreographyVideoReadyVersion(v => v + 1);
  }, []);

  const setCarouselPlaybackSpeedCallback = useCallback(
    (speed: number | "sync") => {
      setCarouselPlaybackSpeedState(clampToCarouselPlaybackSpeed(speed));
    },
    []
  );

  const setCarouselMuted = useCallback((muted: boolean) => {
    setCarouselMutedState(muted);
  }, []);

  const setSessionHighlightVideoAudioMutedCallback = useCallback(
    (muted: boolean) => {
      setSessionHighlightVideoAudioMuted(muted);
    },
    []
  );

  return (
    <VideoSwipeContext.Provider
      value={{
        videoRef,
        choreographyVideoRef,
        choreographyVideoRef2,
        swipeProgress,
        isSwiping,
        isDashboardLoaded,
        isSlowMotion,
        setSwipeProgress,
        setIsSwiping,
        setDashboardLoaded,
        stopVideo,
        closeCarousel,
        playVideoSlowMotion,
        resetVideoSpeed,
        cancelSlowMotion,
        getActiveVideoRef,
        setChoreographyVideoController,
        getChoreographyVideoController,
        setCurrentChoreographyVideoId,
        getCurrentChoreographyVideoId,
        setPrevChoreographyVideoId,
        getPrevChoreographyVideoId,
        setNextChoreographyVideoId,
        getNextChoreographyVideoId,
        choreographySegmentStartTime,
        setChoreographySegmentStartTime,
        choreographySegmentEndTime,
        setChoreographySegmentEndTime,
        choreographyTimeOffset,
        setChoreographyTimeOffset,
        choreographyActiveSlot,
        setChoreographyActiveSlot,
        choreographyVideoReadyVersion,
        notifyChoreographyVideoReady,
        carouselPlaybackSpeed,
        setCarouselPlaybackSpeed: setCarouselPlaybackSpeedCallback,
        carouselMuted,
        setCarouselMuted,
        sessionHighlightVideoAudioMuted,
        setSessionHighlightVideoAudioMuted:
          setSessionHighlightVideoAudioMutedCallback,
        carouselChromeMinTopPx,
        reportCarouselChromeMinTop,
      }}
    >
      {children}
    </VideoSwipeContext.Provider>
  );
}

export function useVideoSwipe() {
  const context = useContext(VideoSwipeContext);
  if (!context) {
    throw new Error("useVideoSwipe must be used within a VideoSwipeProvider");
  }
  return context;
}

export function useVideoSwipeOptional() {
  return useContext(VideoSwipeContext);
}
